import { Alert, CircularProgress, List, ListItemButton, Stack, Typography } from "@mui/material";
import { manifestNumberLabel } from "encamp-shared/src/waste/shippingDocumentLabels";
import { CandidateManifestsForShipmentQuery } from "generated-graphql/graphql";

import { formatShipmentDate } from "./shipmentDisplay";

type CandidateManifest = NonNullable<
  CandidateManifestsForShipmentQuery["processedManifests"]
>["items"][number];

/**
 * The manifests a facility has received, as a selectable list.
 *
 * Rows carry ship date, vendor, destination and line count rather than just the
 * tracking number: two manifests from the same vendor in the same week look
 * identical otherwise, and picking the wrong one writes a false link into the
 * cradle-to-grave record.
 */
export function ManifestPickerList({
  manifests,
  total,
  loading,
  searching,
  selectedId,
  onSelect,
}: {
  manifests: readonly CandidateManifest[];
  total: number;
  loading: boolean;
  /** Whether the empty state should blame the search rather than the facility. */
  searching: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (loading && manifests.length === 0) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (manifests.length === 0) {
    return (
      <Alert severity="info">
        No documents found for this facility{searching ? " matching that search" : ""}. A document
        appears here once it has been parsed.
      </Alert>
    );
  }

  return (
    <>
      <List dense disablePadding sx={{ maxHeight: 320, overflowY: "auto" }}>
        {manifests.map((manifest) => (
          <ListItemButton
            key={manifest.id}
            selected={manifest.id === selectedId}
            onClick={() => onSelect(manifest.id)}
          >
            <Stack sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" noWrap>
                {manifestNumberLabel(manifest)}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {[
                  `Shipped ${formatShipmentDate(manifest.shippedDate)}`,
                  manifest.vendor,
                  manifest.desFacilityName,
                  manifest.wasteLineCount == null
                    ? null
                    : `${manifest.wasteLineCount} ${
                        manifest.wasteLineCount === 1 ? "line" : "lines"
                      }`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </Stack>
          </ListItemButton>
        ))}
      </List>
      {total > manifests.length && (
        <Typography variant="caption" color="text.secondary">
          Showing {manifests.length} of {total}. Narrow the search to see more.
        </Typography>
      )}
    </>
  );
}
